import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '../../utils'

/**
 * Button component with adaptive design and multiple variants
 * Supports mobile-first design with desktop enhancements
 */

const buttonVariants = {
  variant: {
    default: 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500',
    destructive: 'bg-error-500 text-white hover:bg-error-600 focus:ring-error-500',
    outline: 'border border-border-primary bg-white hover:bg-neutral-50 hover:text-primary-600 focus:ring-primary-500',
    secondary: 'bg-secondary-500 text-white hover:bg-secondary-600 focus:ring-secondary-500',
    ghost: 'hover:bg-neutral-100 hover:text-primary-600 focus:ring-primary-500',
    link: 'text-primary-500 underline-offset-4 hover:underline focus:ring-primary-500'
  },
  size: {
    sm: 'h-8 px-3 text-xs',
    default: 'h-10 px-4 py-2 text-sm',
    lg: 'h-12 px-6 text-base',
    xl: 'h-14 px-8 text-lg',
    icon: 'h-10 w-10'
  },
  adaptive: {
    true: 'text-adaptive-sm p-adaptive-sm desktop-enhanced',
    false: ''
  }
}

const Button = React.forwardRef(({
  className,
  variant = 'default',
  size = 'default',
  adaptive = true,
  asChild = false,
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  children,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : 'button'
  
  const baseClasses = cn(
    // Base button styles
    'inline-flex items-center justify-center rounded-md font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    
    // Variant styles
    buttonVariants.variant[variant],
    
    // Size styles (only if not adaptive)
    !adaptive && buttonVariants.size[size],
    
    // Adaptive styles
    adaptive && buttonVariants.adaptive.true,
    
    // Loading state
    loading && 'cursor-wait',
    
    // Desktop enhancements
    'lg:hover:scale-105 lg:active:scale-95',
    'transition-all duration-200 ease-in-out',
    
    className
  )
  
  return (
    <Comp
      className={baseClasses}
      ref={ref}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="mr-2 h-4 w-4 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      
      {leftIcon && !loading && (
        <span className="mr-2 flex-shrink-0">
          {leftIcon}
        </span>
      )}
      
      <span className={cn(
        'flex-1',
        adaptive && 'text-adaptive-sm',
        (leftIcon || rightIcon || loading) && 'mx-1'
      )}>
        {children}
      </span>
      
      {rightIcon && !loading && (
        <span className="ml-2 flex-shrink-0">
          {rightIcon}
        </span>
      )}
    </Comp>
  )
})

Button.displayName = 'Button'

export { Button, buttonVariants }